/**
 * Auto-login utility for Console SSO integration.
 *
 * Supports two strategies depending on the environment:
 *   - Localhost  → reads token + googleId from URL params (_autoLogin, _token, _googleId)
 *   - QA / PROD  → reads shared cookies written by front-pricing-rocket (habi_auth_token, habi_user)
 */

import Cookies from "js-cookie";

const COOKIE_TOKEN = "habi_auth_token";
const COOKIE_USER  = "habi_user";

interface AutoLoginData {
  token: string;
  googleId: string;
}

/**
 * Reads auto-login data from URL params (localhost strategy).
 */
const getAutoLoginFromParams = (): AutoLoginData | null => {
  const params = new URLSearchParams(window.location.search);

  if (params.get("_autoLogin") !== "true") return null;

  const token    = params.get("_token");
  const googleId = params.get("_googleId");

  if (!token || !googleId) {
    console.warn(
      "Auto-login attempted but missing required fields (token, googleId)",
    );
    return null;
  }

  return { token, googleId };
};

/**
 * Reads auto-login data from shared cookies (QA / PROD strategy).
 * Cookies are set by front-pricing-rocket with domain=.llavero.co or .habi.co.
 */
const getAutoLoginFromCookies = (): AutoLoginData | null => {
  const token      = Cookies.get(COOKIE_TOKEN);
  const userCookie = Cookies.get(COOKIE_USER);

  if (!token || !userCookie) return null;

  try {
    const { googleId } = JSON.parse(userCookie) as { googleId: string };
    if (!googleId) return null;
    return { token, googleId };
  } catch {
    console.warn("Auto-login: cookie habi_user has invalid format");
    return null;
  }
};

/**
 * Returns auto-login data from URL params (localhost) or shared cookies (QA/PROD).
 * Returns null if neither source has valid data.
 */
export const getAutoLoginData = (): AutoLoginData | null => {
  return getAutoLoginFromParams() ?? getAutoLoginFromCookies();
};

/**
 * Clears all auto-login data: URL params and shared cookies.
 * Called after a successful or failed SSO login to avoid credential leaks.
 */
export const clearAutoLoginParams = (): void => {
  const url = new URL(window.location.href);

  url.searchParams.delete("_autoLogin");
  url.searchParams.delete("_token");
  url.searchParams.delete("_googleId");

  window.history.replaceState({}, "", url.toString());

  Cookies.remove(COOKIE_TOKEN);
  Cookies.remove(COOKIE_USER);
};

/**
 * Save minimal auto-login data to localStorage.
 * Full user data will be fetched from Cognito using getUserRole API.
 */
export const saveAutoLoginData = (data: AutoLoginData): void => {
  localStorage.setItem("token", data.token);
  localStorage.setItem("googleId", data.googleId);
  localStorage.setItem("ssoLogin", "true");
};

/**
 * Check if the current session was started via SSO from an external tool.
 */
export const isSSOSession = (): boolean => {
  return localStorage.getItem("ssoLogin") === "true";
};

/**
 * Clear the SSO session flag on logout.
 */
export const clearSSOSession = (): void => {
  localStorage.removeItem("ssoLogin");
};
